import React from 'react';
import type { Room } from '../types';

interface RoomContextMenuProps {
  isVisible: boolean;
  position: { x: number; y: number };
  room: Room | null;
  onClose: () => void;
  onRename: (room: Room) => void;
  onSelect: (room: Room) => void;
  onLabel?: (room: Room) => void;
  onDelete?: (room: Room) => void;
  roomLabels?: Record<string, string>;
}

const RoomContextMenu: React.FC<RoomContextMenuProps> = ({
  isVisible,
  position,
  room,
  onClose,
  onRename,
  onSelect,
  onLabel,
  onDelete,
  roomLabels = {},
}) => {
  if (!isVisible || !room) {
    return null;
  }

  const handleRename = () => {
    onRename(room);
    onClose();
  };

  const handleSelect = () => {
    onSelect(room);
    onClose();
  };

  const handleLabel = () => {
    if (onLabel) {
      onLabel(room);
      onClose();
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(room);
      onClose();
    }
  };

  // Check if room has a custom label
  const hasCustomLabel = roomLabels[room.id] && 
                         roomLabels[room.id].trim() !== '' && 
                         !roomLabels[room.id].startsWith('Room ');

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {/* Invisible backdrop to close menu when clicking outside */}
      <div
        className="fixed inset-0 z-[999]"
        onClick={handleBackdropClick}
      />
      
      {/* Context menu */}
      <div
        className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-[1000] min-w-[200px] max-w-[250px]"
        style={{
          left: `${Math.min(position.x, window.innerWidth - 250)}px`,
          top: `${Math.min(position.y, window.innerHeight - 200)}px`,
        }}
      >
        <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
          {room.name || `Room ${room.id}`}
        </div>
        
        <button
          onClick={handleSelect}
          className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center gap-3 touch-manipulation"
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
            />
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" 
            />
          </svg>
          Select Room
        </button>

        {!hasCustomLabel && onLabel && (
          <button
            onClick={handleLabel}
            className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center gap-3 touch-manipulation"
          >
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" 
              />
            </svg>
            Add Label
          </button>
        )}
        
        <button
          onClick={handleRename}
          className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center gap-3 touch-manipulation"
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" 
            />
          </svg>
          {hasCustomLabel ? 'Edit Label' : 'Rename Room'}
        </button>

        {onDelete && (
          <button
            onClick={handleDelete}
            className="w-full text-left px-4 py-3 text-base text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors flex items-center gap-3 touch-manipulation"
          >
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
              />
            </svg>
            Delete Room
          </button>
        )}
        
        <div className="border-t border-gray-100 mt-2 pt-2">
          <div className="px-3 py-1 text-xs text-gray-500">
            Area: {room.area?.toFixed(2) || '0'} sq ft
          </div>
          {room.stableId && (
            <div className="px-3 py-1 text-xs text-gray-400">
              ID: {room.stableId}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RoomContextMenu; 